import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { reportsApi } from '../api';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { format } from 'date-fns';
import './Dashboard.css';

const STATUS_COLORS = {
  pending: '#f59e0b',
  stitching: '#3b82f6',
  qc: '#8b5cf6',
  ready: '#10b981',
  delivered: '#6b7280',
  cancelled: '#ef4444',
};

function StatCard({ icon, label, value, color, to }) {
  const card = (
    <div className="stat-card" style={{ '--card-accent': color }}>
      <div className="stat-icon" style={{ background: `${color}18`, color }}>
        {icon}
      </div>
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
  return to ? <Link to={to} style={{ textDecoration: 'none' }}>{card}</Link> : card;
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [plData, setPlData] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [dashRes, plRes] = await Promise.all([
          reportsApi.getDashboard(),
          reportsApi.getProfitLoss({
            startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
            endDate: new Date().toISOString().slice(0, 10),
          }),
        ]);
        setData(dashRes.data.data);
        setPlData(plRes.data.data.daily || []);
      } catch { }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return (
    <div className="page-container">
      <div className="loading-screen" style={{ minHeight: '60vh' }}>
        <div className="spinner" />
      </div>
    </div>
  );

  const orderStatus = data?.ordersByStatus || [];

  return (
    <div className="page-container dashboard-page">
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <span className="text-muted text-sm">Real-time business overview</span>
      </div>

      {/* KPI Grid */}
      <div className="grid-4 mb-6">
        <StatCard icon="📋" label="Active Orders" value={data?.activeOrders ?? 0} color="#6366f1" to="/orders" />
        <StatCard icon="🚚" label="Today's Deliveries" value={data?.todayDeliveries ?? 0} color="#10b981" to="/orders?dueToday=true" />
        <StatCard icon="⚠️" label="Low Stock Alerts" value={data?.lowStockCount ?? 0} color="#f59e0b" to="/inventory?lowStock=true" />
        <StatCard icon="👥" label="Total Customers" value={data?.totalCustomers ?? 0} color="#3b82f6" to="/customers" />
      </div>

      <div className="grid-4 mb-6">
        <StatCard icon="💰" label="Monthly Revenue" value={`₹${Number(data?.monthlyRevenue || 0).toLocaleString('en-IN')}`} color="#8b5cf6" />
        <StatCard icon="📈" label="Monthly Profit" value={`₹${Number(data?.monthlyProfit || 0).toLocaleString('en-IN')}`} color="#10b981" />
        <StatCard icon="⏳" label="Pending Items" value={data?.pendingItems ?? 0} color="#f59e0b" />
        <StatCard icon="✂️" label="Active Tailors" value={(data?.ordersByStatus || []).length > 0 ? '—' : '—'} color="#ec4899" to="/tailors" />
      </div>

      {/* Charts Row */}
      <div className="grid-2 mb-6">
        {/* Revenue Chart */}
        <div className="card">
          <div className="card-header">
            <h3>Revenue & Profit (30 days)</h3>
          </div>
          <div className="card-body chart-body">
            {plData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={plData}>
                  <defs>
                    <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="profGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 11 }} tickFormatter={(d) => format(new Date(d), 'dd MMM')} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8, color: '#1e293b' }} formatter={(v, n) => [`₹${Number(v).toLocaleString('en-IN')}`, n === 'revenue' ? 'Revenue' : 'Profit']} />
                  <Area type="monotone" dataKey="revenue" stroke="#6366f1" fill="url(#revGrad)" strokeWidth={2} />
                  <Area type="monotone" dataKey="profit" stroke="#10b981" fill="url(#profGrad)" strokeWidth={2} />
                  <Legend formatter={(v) => v === 'revenue' ? 'Revenue' : 'Profit'} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-state" style={{ padding: '2rem' }}>
                <div className="empty-icon">📊</div>
                <p>No revenue data for this period</p>
              </div>
            )}
          </div>
        </div>

        {/* Order Status Pie */}
        <div className="card">
          <div className="card-header">
            <h3>Orders by Status</h3>
          </div>
          <div className="card-body chart-body">
            {orderStatus.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={orderStatus} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={80} label={({ status, percent }) => `${status} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={11}>
                    {orderStatus.map((entry) => (
                      <Cell key={entry.status} fill={STATUS_COLORS[entry.status] || '#6366f1'} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8, color: '#1e293b' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-state" style={{ padding: '2rem' }}>
                <div className="empty-icon">📋</div>
                <p>No orders yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="card">
        <div className="card-header">
          <h3>Recent Orders</h3>
          <Link to="/orders" className="btn btn-secondary btn-sm">View All</Link>
        </div>
        <div className="table-wrapper" style={{ borderRadius: 0, border: 'none' }}>
          <table>
            <thead>
              <tr>
                <th>Order #</th>
                <th>Customer</th>
                <th>Status</th>
                <th>Amount</th>
                <th>Due Date</th>
              </tr>
            </thead>
            <tbody>
              {(data?.recentOrders || []).length === 0 ? (
                <tr><td colSpan={5} className="text-center text-muted" style={{ padding: '2rem' }}>No orders yet</td></tr>
              ) : data.recentOrders.map((order) => (
                <tr key={order.id}>
                  <td><Link to={`/orders/${order.id}`} className="mono text-sm" style={{ color: 'var(--accent-light)' }}>{order.orderNumber}</Link></td>
                  <td>{order.customer?.name}</td>
                  <td><span className={`badge badge-${order.status}`}>{order.status}</span></td>
                  <td className="font-semibold">₹{Number(order.totalSellingPrice).toLocaleString('en-IN')}</td>
                  <td className="text-muted text-sm">{order.dueDate || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
